import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * EsgInteriorDescriptionLogistics Widget
 */
@Component({
  selector: 'esg-interior-description-logistics-widget',
  templateUrl: './esg-interior-description-logistics-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './esg-interior-description-logistics-widget.component.scss']
})
export class EsgInteriorDescriptionLogisticsWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Add row subject
   */
  private addRowSubject = new Subject<any>();

  /**
   * Add row observable
   */
  public addRow$ = this.addRowSubject.asObservable();

  /**
   * Delete current row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Text & List section rows
   */
  rows: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionName: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'esgInteriorDescriptionLogistics', false);
    this.textAndListSectionName = `${this.name}`;//should be the same as defined in repository
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("esgInteriorDescriptionLogistics", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }

    this.initializeSection();
    this.loadReflist('ref_l3_esg_interior_description_logistics');

    this.mockupData$ = this.widgetService.loadMockup('esgInteriorDescriptionLogistics').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('esgInteriorDescriptionLogistics', this.getReport()).pipe();
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Initializes the Text & List section
   */
  private initializeSection() {
    this.rows = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionName, [
      {
        topicId: 1,
        fieldName: 'clearHeight',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 2,
        fieldName: 'floorLoadCapacity',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 3,
        fieldName: 'overheadDoorsDriveInDoors',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 12,
        fieldName: 'loadingDocksDockLevelDoors',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 13,
        fieldName: 'coldStorage',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 4,
        fieldName: 'sprinklerInstallation',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 5,
        fieldName: 'manoeuvringSpace',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 6,
        fieldName: 'powerCapacity',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 7,
        fieldName: 'loadingCapacityOfTheRoof',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 8,
        fieldName: 'regulatoryAuthorisation',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 9,
        fieldName: 'security',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 10,
        fieldName: 'internalConditions',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      },
      {
        topicId: 11,
        fieldName: 'additionalComments',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 1,
        selected: true
      }
    ]
    );
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   */
  public deleteCurrentRow(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.deleteCurrentRowSubject.next();
  }

  /**
   * Add row event handler
   */
  public addRow(): void {
    const unselectedFields = this.rows.filter(item => !item.selected);
    if (unselectedFields.length > 0) {
      this.setTextAndListSettings(this.textAndListSectionName, this.rows);
      this.addRowSubject.next({
        topicId: this.getTopicId(),
        fieldName: null,
        isNumericField: false,
        isTextField: true,
        lastTopicId: this.getTopicId(),
        selected: true
      });
    }
  }

  /**
   * Move current row up event handler
   */
  public moveCurrentRowUp(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowUpSubject.next();
  }

  /**
   * Move current row down event handler
   */
  public moveCurrentRowDown(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowDownSubject.next();
  }

  /**
   * Data changed event handler
   */
  public onDataChanged(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   */
  private getTopicId() {
    const unselectedFields = this.rows.filter(item => !item.selected);
    return Math.min(...unselectedFields.map(item => item.topicId));
  }
}
